import React, { useState } from 'react';

// Checkbox Component
const CheckBox = ({
  options = [],
  selectedValues = [],
  onChange = () => {},
  groupLabel = '',
  disabled = false,
  layout = 'vertical', // 'vertical', 'horizontal', 'grid'
  size = 'medium', // 'small', 'medium', 'large'
  color = '#3b82f6',
  className = ''
}) => {
  const handleCheckboxChange = (value, isChecked) => {
    if (disabled) return;

    let newValues;
    if (isChecked) {
      newValues = selectedValues.includes(value) 
        ? selectedValues 
        : [...selectedValues, value];
    } else {
      newValues = selectedValues.filter(v => v !== value);
    }
    onChange(newValues);
  };

  const sizeClasses = {
    small: 'checkbox-small',
    medium: 'checkbox-medium',
    large: 'checkbox-large'
  };

  const layoutClasses = {
    vertical: 'checkbox-vertical',
    horizontal: 'checkbox-horizontal',
    grid: 'checkbox-grid'
  };

  return (
    <>
      <style>{`
        .checkbox-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .checkbox-group-label {
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
          font-size: 16px;
        }

        .checkbox-group-label.disabled {
          color: #9ca3af;
        }

        .checkbox-vertical {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .checkbox-horizontal {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }

        .checkbox-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 8px;
          border-radius: 8px;
          position: relative;
        }

        .checkbox-item:hover:not(.disabled) {
          background: #f8fafc;
        }

        .checkbox-item.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .checkbox-input {
          position: absolute;
          opacity: 0;
          cursor: pointer;
        }

        .checkbox-custom {
          position: relative;
          border: 2px solid #d1d5db;
          border-radius: 4px;
          background: white;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .checkbox-small .checkbox-custom {
          width: 16px;
          height: 16px;
        }

        .checkbox-medium .checkbox-custom {
          width: 20px;
          height: 20px;
        }

        .checkbox-large .checkbox-custom {
          width: 24px;
          height: 24px;
        }

        .checkbox-custom:hover:not(.disabled) {
          border-color: var(--checkbox-color);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .checkbox-custom.checked {
          background: var(--checkbox-color);
          border-color: var(--checkbox-color);
        }

        .checkbox-custom.disabled {
          background: #f9fafb;
          border-color: #e5e7eb;
        }

        .checkbox-checkmark {
          color: white;
          opacity: 0;
          transition: opacity 0.2s ease;
          font-weight: bold;
        }

        .checkbox-small .checkbox-checkmark {
          font-size: 10px;
        }

        .checkbox-medium .checkbox-checkmark {
          font-size: 12px;
        }

        .checkbox-large .checkbox-checkmark {
          font-size: 14px;
        }

        .checkbox-custom.checked .checkbox-checkmark {
          opacity: 1;
        }

        .checkbox-label {
          margin-left: 12px;
          color: #374151;
          transition: color 0.2s ease;
          flex: 1;
        }

        .checkbox-small .checkbox-label {
          font-size: 14px;
        }

        .checkbox-medium .checkbox-label {
          font-size: 16px;
        }

        .checkbox-large .checkbox-label {
          font-size: 18px;
        }

        .checkbox-label.disabled {
          color: #9ca3af;
        }

        @media (max-width: 768px) {
          .checkbox-horizontal {
            flex-direction: column;
          }
          
          .checkbox-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div 
        className={`checkbox-container ${className}`}
        style={{ '--checkbox-color': color }}
      >
        {groupLabel && (
          <div className={`checkbox-group-label ${disabled ? 'disabled' : ''}`}>
            {groupLabel}
          </div>
        )}
        
        <div className={`${layoutClasses[layout]} ${sizeClasses[size]}`}>
          {options.map((option, index) => {
            const value = typeof option === 'string' ? option : option.value;
            const label = typeof option === 'string' ? option : option.label;
            const isDisabled = disabled || (typeof option === 'object' && option.disabled);
            const isChecked = selectedValues.includes(value);
            
            return (
              <label
                key={index}
                className={`checkbox-item ${isDisabled ? 'disabled' : ''}`}
              >
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={isChecked}
                  onChange={(e) => handleCheckboxChange(value, e.target.checked)}
                  disabled={isDisabled}
                />
                <div className={`checkbox-custom ${isChecked ? 'checked' : ''} ${isDisabled ? 'disabled' : ''}`}>
                  <span className="checkbox-checkmark">✓</span>
                </div>
                <span className={`checkbox-label ${isDisabled ? 'disabled' : ''}`}>
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
};


export default CheckBox;